import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Plus, Trash2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CITIES } from "@/data/content";
import api, { formatApiErrorDetail } from "@/lib/api";

const cityName = (slug) => CITIES.find((c) => c.slug === slug)?.name || "Région (auto)";

export default function EditorialCalendar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [city, setCity] = useState("auto");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/editorial");
      setItems(data);
    } catch {
      toast.error("Impossible de charger le calendrier éditorial.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!topic.trim() || !date) {
      toast.error("Sujet et date requis.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/editorial", {
        topic: topic.trim(),
        city_slug: city === "auto" ? null : city,
        target_date: date,
      });
      toast.success("Sujet planifié.");
      setTopic("");
      setDate("");
      setCity("auto");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/editorial/${id}`);
      toast.success("Sujet supprimé.");
      load();
    } catch {
      toast.error("Échec de la suppression.");
    }
  };

  const planned = items.filter((i) => i.status === "planifié");

  return (
    <div
      className="rounded-2xl border border-brand-line bg-white p-6 shadow-sm mb-8"
      data-testid="editorial-calendar"
    >
      <div className="flex items-center gap-2 mb-1">
        <CalendarRange className="w-5 h-5 text-brand-golddark" />
        <h3 className="font-display font-semibold text-lg text-brand-navy">
          Calendrier éditorial
        </h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Planifiez vos sujets : chaque lundi, l'IA rédige en priorité le sujet planifié
        le plus ancien. Sans sujet planifié, la rotation automatique par ville prend le relais.
      </p>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <Input
          data-testid="editorial-topic-input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Sujet de l'article (ex : sécuriser sa maison avant les vacances)"
          className="flex-1"
        />
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="md:w-56" data-testid="editorial-city-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto" data-testid="editorial-city-auto">
              Région (ville auto)
            </SelectItem>
            {CITIES.map((c) => (
              <SelectItem key={c.slug} value={c.slug} data-testid={`editorial-city-${c.slug}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          data-testid="editorial-date-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="md:w-44"
        />
        <Button
          onClick={add}
          disabled={saving}
          data-testid="editorial-add-btn"
          className="bg-brand-navy hover:bg-brand-navy2 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Planifier
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="editorial-empty">
          Aucun sujet planifié — la rotation automatique par ville est active.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="editorial-list">
          {items.map((i) => (
            <li
              key={i.id}
              data-testid={`editorial-item-${i.id}`}
              className="flex items-center justify-between gap-4 rounded-xl border border-brand-line px-4 py-3"
            >
              <div className="min-w-0">
                <span className="block font-medium text-brand-navy truncate">{i.topic}</span>
                <span className="text-xs text-slate-500">
                  {cityName(i.city_slug)} · semaine du{" "}
                  {new Date(i.target_date + "T00:00:00").toLocaleDateString("fr-FR")}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge
                  data-testid={`editorial-status-${i.id}`}
                  className={
                    "rounded-full border-0 " +
                    (i.status === "planifié"
                      ? "bg-brand-gold text-brand-navy"
                      : "bg-emerald-100 text-emerald-700")
                  }
                >
                  {i.status}
                </Badge>
                <button
                  onClick={() => remove(i.id)}
                  data-testid={`editorial-delete-${i.id}`}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Supprimer le sujet"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {planned.length > 0 && (
        <p className="text-xs text-slate-400 mt-3">
          {planned.length} sujet{planned.length > 1 ? "s" : ""} en file — prochain : «{" "}
          {planned[0].topic} ».
        </p>
      )}
    </div>
  );
}
